import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { CreateBankFormData, createBankSchema } from '../../../services/properties/schema/CreateBankSchema';
import { useForm } from 'react-hook-form';
import clsx from 'clsx';
import { useCreateBank } from '../../../services/properties/hooks/useCreateBank';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface CreateBankAccountDialogProps {
    show: boolean;
    onClose: () => void;
    propertyId: string;
}

const CreateBankAccountDialog: React.FC<CreateBankAccountDialogProps> = ({ show, onClose, propertyId }) => {
    const { register, handleSubmit, formState: { errors }, reset, setError } = useForm<CreateBankFormData>({
        resolver: zodResolver(createBankSchema)
    });

    useEffect(() => {
        if (propertyId) {
            reset({ property_id: propertyId });
        }
    }, [propertyId]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const { mutate, isPending } = useCreateBank();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((data: CreateBankFormData) => {
        toast.loading('Creating bank account...', { id: 'create-bank-account' });

        mutate(data, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['banks'].includes(key as string))
                });

                toast.success('Bank account created successfully', { id: 'create-bank-account' });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof CreateBankFormData, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to create bank account', { id: 'create-bank-account' });
                    }
                } else {
                    toast.error(mutationError?.message || 'Failed to create bank account', { id: 'create-bank-account' });
                }
            }
        });
    }, [mutate]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            className="custom-z-modal"
            dialogClassName="custom-z-dialog"
        >
            <Modal.Header closeButton>
                <Modal.Title>Create Bank Account</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <input type="hidden" {...register('property_id')} />
                    <div className="mb-3">
                        <label htmlFor="bankName" className="form-label">Account Name</label>
                        <input type="text" className={clsx('form-control', { 'is-invalid': errors.account_name })} id="bankName" {...register('account_name')} />
                        {errors.account_name && <div className="invalid-feedback">{errors.account_name.message}</div>}
                    </div>
                    <div className="mb-3">
                        <label htmlFor="bankName" className="form-label">Account Number</label>
                        <input type="text" className={clsx('form-control', { 'is-invalid': errors.account_number })} id="bankName" {...register('account_number')} />
                        {errors.account_number && <div className="invalid-feedback">{errors.account_number.message}</div>}
                    </div>
                    <div className='mb-3'>
                        <label htmlFor="bankName" className="form-label">Bank Name</label>
                        <input type="text" className={clsx('form-control', { 'is-invalid': errors.bank_name })} id="bankName" {...register('bank_name')} />
                        {errors.bank_name && <div className="invalid-feedback">{errors.bank_name.message}</div>}
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-secondary" onClick={handleClose}>Close</button>
                <button type='submit' className="btn btn-primary" onClick={handleSubmit(onSubmit)} disabled={isPending}>Create</button>
            </Modal.Footer>
        </Modal>
    )
}

export default CreateBankAccountDialog